import {IconButton} from '@wandb/components';
import {isMac} from '@wandb/weave/common/util/browser';
import React from 'react';
import TimeAgo from 'react-timeago';
import {twMerge} from 'tailwind-merge';

import {HOTKEYS, HOTKEYS_FALLBACK} from '../../../util/hotkeys';
import {KeyboardHintInTooltip} from '../../BetaFeatures/KeyboardHInt';
import {useUndoRedo} from '../hooks/useUndoRedo';
import {useViewBarContext} from '../NamedWorkspaceViewBarContext';

const undoHotkeys = isMac
  ? HOTKEYS.undoWorkspaceAction
  : HOTKEYS_FALLBACK.undoWorkspaceAction;
const redoHotkeys = isMac
  ? HOTKEYS.redoWorkspaceAction
  : HOTKEYS_FALLBACK.redoWorkspaceAction;

export const NamedWorkspaceUndoRedo = () => {
  const {currentView, undo, redo} = useUndoRedo();

  if (currentView == null) {
    return null;
  }

  return (
    <div className="flex gap-2">
      <IconButton
        data-test="nw-undo-button"
        icon="undo"
        variant="secondary"
        aria-label="Undo last action"
        isDisabled={!undo}
        onPress={() => undo?.(false)}
        tooltip={{
          placement: 'bottom',
          children: <KeyboardHintInTooltip text="Undo" keys={undoHotkeys} />,
        }}
      />
      <IconButton
        data-test="nw-redo-button"
        icon="redo"
        variant="secondary"
        aria-label="Redo last action"
        isDisabled={!redo}
        onPress={() => redo?.(false)}
        tooltip={{
          placement: 'bottom',
          children: <KeyboardHintInTooltip text="Redo" keys={redoHotkeys} />,
        }}
      />
    </div>
  );
};

export const NamedWorkspaceTimestamp = ({className}: {className: string}) => {
  const {currentView, currentWorkspaceNwId} = useViewBarContext();

  if (currentView == null) {
    return null;
  }

  if (currentView.saving) {
    return (
      <p
        className={twMerge(
          'text-base font-normal leading-[22px] text-moon-500',
          className
        )}>
        Saving...
      </p>
    );
  }

  const workspaceUpdatedAt = currentView.updatedAt;

  if (currentView.id == null || !workspaceUpdatedAt) {
    return (
      <p
        className={twMerge(
          'text-base font-normal leading-[22px] text-moon-500',
          className
        )}>
        Changes saved automatically
      </p>
    );
  }

  return (
    <div
      className={twMerge(
        'text-base font-normal leading-[22px] text-moon-500',
        className
      )}>
      <p>
        Saved{' '}
        <span>
          <TimeAgo
            minPeriod={10}
            formatter={(value, unit, suffix, epochSeconds, nextFormatter) =>
              unit === 'second'
                ? 'just now'
                : nextFormatter!(value, unit, suffix, epochSeconds)
            }
            date={workspaceUpdatedAt}
          />
        </span>
      </p>
    </div>
  );
};
